import { useCallback, useEffect, useRef } from 'react';

import { Tracking } from '@/shared/services/tracking';

import { WEBRTC_FLUSH_EVERY_SAMPLES, WEBRTC_POINT_EVERY_SAMPLES } from '../constants';
import { createBitrateCalculator } from '../services/calculateBitrate';

/**
 * Headless WebRTC metrics collector.
 *
 * Design goals (see the discussion in the PR):
 *  - Gather the *maximum* set of useful metrics per sample. Once `getStats()` has been
 *    paid for, reading 15 fields costs the same as reading 4 — so richness is free.
 *  - Do NOT touch React state. `collect()` runs on every stats sample (~1 Hz) but only
 *    mutates refs, so it triggers zero re-renders. The live chart overlay is a separate,
 *    on-demand consumer.
 *  - Aggregate into points: every WEBRTC_POINT_EVERY_SAMPLES samples, gauges are averaged
 *    and counters reported as the delta over the point. Points are buffered and sent every
 *    WEBRTC_FLUSH_EVERY_SAMPLES samples, so resolution doesn't cost extra requests.
 *
 * Emits one `WEBRTC_METRIC` event per metric per flush (`{ name, samples: [ts, value][] }`,
 * ts in browser ms at the end of each point) — the backend attaches the authenticated email,
 * and the scheduler writes each point as a `webrtc_<name>` measurement.
 */

type MetricKind = 'gauge' | 'counter';

// Gauges are averaged; counters (monotonic totals) are reported as a per-point delta.
const METRIC_KINDS: Record<string, MetricKind> = {
  bitrate: 'gauge',
  fps: 'gauge',
  jitter: 'gauge',
  jitterBufferMs: 'gauge',
  rtt: 'gauge',
  availableIncomingBitrate: 'gauge',
  frameHeight: 'gauge',
  packetsLost: 'counter',
  nackCount: 'counter',
  pliCount: 'counter',
  framesDropped: 'counter',
  framesDecoded: 'counter',
  keyFramesDecoded: 'counter',
  freezeCount: 'counter',
  freezeDuration: 'counter',
};

const round2 = (n: number): number => Number.parseFloat(n.toFixed(2));

const isNum = (v: unknown): v is number => typeof v === 'number' && !Number.isNaN(v);

const addPoint = (points: Map<string, [number, number][]>, name: string, ts: number, value: number) => {
  const series = points.get(name) ?? [];
  series.push([ts, round2(value)]);
  points.set(name, series);
};

// TEMPORARY debug tracing (unconditional, uses console.log so it shows at the default
// devtools log level). Revert to a gated console.debug once the pipeline is confirmed.
const wlog = (...args: unknown[]): void => {
  console.debug('[webrtc:collector]', ...args);
};

/**
 * Flatten a raw RTCStatsReport into a `{ metricName: value }` sample.
 * Only fields the browser actually reports are included, so absent stats simply don't
 * contribute to their window (rather than polluting it with zeros).
 */
function extractSample(
  report: RTCStatsReport,
  calcBitrate: (stats: { bytesReceived?: number }) => number,
): Record<string, number> {
  const sample: Record<string, number> = {};

  report.forEach((raw) => {
    const r = raw as any;

    if (r.type === 'inbound-rtp' && r.mediaType === 'video') {
      const bps = calcBitrate(r);
      sample.bitrate = round2(bps / 1_000_000); // Mbps

      if (isNum(r.framesPerSecond)) sample.fps = r.framesPerSecond;
      if (isNum(r.jitter)) sample.jitter = r.jitter * 1000; // s -> ms
      if (isNum(r.packetsLost)) sample.packetsLost = r.packetsLost;
      if (isNum(r.nackCount)) sample.nackCount = r.nackCount;
      if (isNum(r.pliCount)) sample.pliCount = r.pliCount;
      if (isNum(r.framesDropped)) sample.framesDropped = r.framesDropped;
      if (isNum(r.framesDecoded)) sample.framesDecoded = r.framesDecoded;
      if (isNum(r.keyFramesDecoded)) sample.keyFramesDecoded = r.keyFramesDecoded;
      if (isNum(r.freezeCount)) sample.freezeCount = r.freezeCount;
      if (isNum(r.totalFreezesDuration)) sample.freezeDuration = r.totalFreezesDuration;
      if (isNum(r.frameHeight)) sample.frameHeight = r.frameHeight;

      // jitterBufferDelay is cumulative seconds; divide by emitted count for avg ms.
      if (isNum(r.jitterBufferDelay) && isNum(r.jitterBufferEmittedCount) && r.jitterBufferEmittedCount > 0) {
        sample.jitterBufferMs = (r.jitterBufferDelay / r.jitterBufferEmittedCount) * 1000;
      }
    }

    // Active ICE candidate pair carries round-trip time and the negotiated incoming bitrate.
    if (r.type === 'candidate-pair' && (r.nominated || r.state === 'succeeded')) {
      if (isNum(r.currentRoundTripTime)) sample.rtt = r.currentRoundTripTime * 1000; // s -> ms
      if (isNum(r.availableIncomingBitrate)) {
        sample.availableIncomingBitrate = round2(r.availableIncomingBitrate / 1_000_000); // Mbps
      }
    }
  });

  return sample;
}

export const useWebrtcMetricsCollector = () => {
  // Per-mount bitrate calculator so its bytesReceived deltas never collide with the chart's.
  const calcBitrateRef = useRef(createBitrateCalculator());

  // Samples of the point being built: gauge values, and each counter's latest total.
  const gaugeSamples = useRef<Map<string, number[]>>(new Map());
  const counterLatest = useRef<Map<string, number>>(new Map());
  const counterBaseline = useRef<Map<string, number>>(new Map());
  const samplesInPoint = useRef(0);
  // Finished points waiting for the next flush, per metric.
  const points = useRef<Map<string, [number, number][]>>(new Map());
  const sampleCount = useRef(0);

  const closePoint = useCallback(() => {
    if (samplesInPoint.current === 0) return;
    const ts = Date.now();

    // Gauges: average of the point's samples.
    gaugeSamples.current.forEach((values, name) => {
      if (values.length > 0) addPoint(points.current, name, ts, values.reduce((acc, v) => acc + v, 0) / values.length);
    });
    gaugeSamples.current.clear();

    // Counters: delta since the last point, then re-baseline.
    counterLatest.current.forEach((latest, name) => {
      const baseline = counterBaseline.current.get(name) ?? latest;
      // Clamp negatives: a connection reset rewinds monotonic counters.
      addPoint(points.current, name, ts, Math.max(0, latest - baseline));
      counterBaseline.current.set(name, latest);
    });

    samplesInPoint.current = 0;
  }, []);

  const flush = useCallback(() => {
    const tracking = Tracking.getInstance();
    points.current.forEach((samples, name) => tracking.track('WEBRTC_METRIC', { name, samples }));
    wlog(`flush → emitting ${points.current.size} WEBRTC_METRIC events`, Object.fromEntries(points.current));
    points.current.clear();
  }, []);

  const collect = useCallback((report: RTCStatsReport | null | undefined) => {
    if (!report || typeof report.forEach !== 'function') {
      wlog('collect() called with no usable stats report', report);
      return;
    }

    const sample = extractSample(report, calcBitrateRef.current);
    wlog(`sample #${sampleCount.current + 1}`, sample);

    for (const [name, value] of Object.entries(sample)) {
      const kind = METRIC_KINDS[name] ?? 'gauge';

      if (kind === 'gauge') {
        const values = gaugeSamples.current.get(name) ?? [];
        values.push(value);
        gaugeSamples.current.set(name, values);
      } else {
        counterLatest.current.set(name, value);
        // Anchor the baseline on first observation so the first flush is a true interval delta.
        if (!counterBaseline.current.has(name)) counterBaseline.current.set(name, value);
      }
    }

    sampleCount.current += 1;
    samplesInPoint.current += 1;
    if (sampleCount.current % WEBRTC_POINT_EVERY_SAMPLES === 0) {
      closePoint();
    }
    if (sampleCount.current % WEBRTC_FLUSH_EVERY_SAMPLES === 0) {
      flush();
    }
  }, [closePoint, flush]);

  // Flush whatever is buffered when the player unmounts, so we don't drop a partial point.
  useEffect(() => {
    wlog(`collector mounted (point every ${WEBRTC_POINT_EVERY_SAMPLES} samples, flush every ${WEBRTC_FLUSH_EVERY_SAMPLES})`);
    return () => {
      wlog('collector unmounting — final flush');
      closePoint();
      flush();
    };
  }, [closePoint, flush]);

  return { collect };
};

export default useWebrtcMetricsCollector;
